/**
 * Phase 4 — Site Admin Grants matrix UI.
 *
 * Mounted inside each expanded site row on /admin/sites by Super Admins.
 *
 * Surface
 *   • Lists existing Site Admin grants for the site (name + email + ops count).
 *   • "Promote a user" dialog: pick from eligible-users (excludes existing
 *     Site Admins of OTHER sites; excludes Super Admins) and toggle the
 *     allowed_operations matrix per module.
 *   • "Edit" reuses the same dialog pre-filled with the grant.
 *   • "Revoke" demotes the user back to Staff.
 *
 * Data model (mirrors backend `routes/site_admin_grants.py`):
 *   { site_id, user_id, user_email, allowed_operations: {module_id: [op_ids]}, modules: [] }
 */
import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Plus, Loader2, ShieldCheck, Trash2, Pencil, UserCheck,
  CheckSquare, Square, Search,
} from 'lucide-react';

export default function SiteAdminGrants({ siteId, siteName }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/site-admin-grants/${siteId}`);
      setGrants(r.data?.grants || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load grants for ${siteId}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [siteId]);

  const onRevoke = async (grant) => {
    if (!window.confirm(
      `Revoke Site Admin access for ${grant.user_email || grant.user_id} on ${siteName}? They will be demoted to "Staff".`
    )) return;
    try {
      await apiClient.delete(`/site-admin-grants/${siteId}/${grant.user_id}`);
      toast.success(`Revoked grant for ${grant.user_email || grant.user_id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  return (
    <div className="space-y-3" data-testid={`grants-panel-${siteId}`}>
      <div className="flex items-center gap-2 text-xs">
        <ShieldCheck className="h-4 w-4 text-stone-500" />
        <span className="font-semibold text-stone-700">Site Admin grants</span>
        <Badge variant="outline" className="text-[10px]">
          {grants.length} {grants.length === 1 ? 'admin' : 'admins'}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto h-7"
          onClick={() => { setEditingGrant(null); setShowDialog(true); }}
          data-testid={`add-grant-btn-${siteId}`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Site Admin
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-stone-500 py-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading grants…
        </div>
      ) : grants.length === 0 ? (
        <div className="text-xs text-stone-500 italic py-3">
          No Site Admins assigned. Click <strong>Add Site Admin</strong> to promote an existing user.
        </div>
      ) : (
        <div className="rounded-md border border-stone-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 text-stone-600">
              <tr>
                <th className="text-left p-2">User</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Modules</th>
                <th className="text-left p-2">Operations</th>
                <th className="text-left p-2">Granted</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const opsCount = Object.values(g.allowed_operations || {})
                  .reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
                return (
                  <tr key={g.grant_id} className="border-t border-stone-100" data-testid={`grant-row-${g.user_id}`}>
                    <td className="p-2 font-semibold">{g.user_name || g.user_id}</td>
                    <td className="p-2 text-stone-600 font-mono text-[11px]">{g.user_email}</td>
                    <td className="p-2">{(g.modules || []).length || <span className="text-stone-400">—</span>}</td>
                    <td className="p-2">{opsCount || <span className="text-stone-400">—</span>}</td>
                    <td className="p-2 text-stone-500 font-mono text-[10px]">
                      {(g.granted_at || '').slice(0, 10)}
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm" variant="outline" className="h-7 mr-1"
                        onClick={() => { setEditingGrant(g); setShowDialog(true); }}
                        data-testid={`edit-grant-${g.user_id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm" variant="outline" className="h-7 text-rose-700 border-rose-300 hover:bg-rose-50"
                        onClick={() => onRevoke(g)}
                        data-testid={`revoke-grant-${g.user_id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Revoke
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showDialog && (
        <GrantDialog
          siteId={siteId}
          siteName={siteName}
          grant={editingGrant}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// GrantDialog — pick user (if creating) + module/operation matrix toggle.
// ---------------------------------------------------------------------------
function GrantDialog({ siteId, siteName, grant, onClose, onSaved }) {
  const isEdit = !!grant;
  const [modules, setModules] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allowed, setAllowed] = useState({}); // { module_id: Set<op_id> }
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);

  useEffect(() => {
    (async () => {
      setLoadingMeta(true);
      try {
        const reqs = [apiClient.get('/admin/modules')];
        if (!isEdit) reqs.push(apiClient.get(`/site-admin-grants/${siteId}/eligible-users`));
        const responses = await Promise.all(reqs);
        const mods = responses[0].data?.modules || [];
        setModules(mods);
        if (!isEdit) {
          setEligibleUsers(responses[1].data?.users || []);
        }
        // Pre-fill matrix.
        const init = {};
        const src = grant?.allowed_operations || {};
        for (const m of mods) {
          const ops = Array.isArray(src[m.module_id]) ? src[m.module_id] : [];
          init[m.module_id] = new Set(ops);
        }
        setAllowed(init);
        if (isEdit) {
          setSelectedUser({
            user_id: grant.user_id, name: grant.user_name, email: grant.user_email,
          });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load matrix metadata');
      } finally {
        setLoadingMeta(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, isEdit]);

  const toggleOp = (moduleId, opId) => {
    setAllowed((prev) => {
      const next = { ...prev };
      const set = new Set(next[moduleId] || []);
      if (set.has(opId)) set.delete(opId); else set.add(opId);
      next[moduleId] = set;
      return next;
    });
  };

  const toggleModuleAll = (mod) => {
    setAllowed((prev) => {
      const next = { ...prev };
      const set = new Set(next[mod.module_id] || []);
      const allOpIds = (mod.operations || []).map((o) => o.op_id);
      const allFilled = allOpIds.every((id) => set.has(id));
      if (allFilled) {
        // un-set all
        for (const id of allOpIds) set.delete(id);
      } else {
        for (const id of allOpIds) set.add(id);
      }
      next[mod.module_id] = set;
      return next;
    });
  };

  const totalOps = useMemo(() =>
    Object.values(allowed).reduce((s, set) => s + (set?.size || 0), 0),
    [allowed],
  );

  const onSave = async () => {
    if (!selectedUser) {
      toast.error('Pick a user to promote');
      return;
    }
    const allowed_operations = {};
    const moduleIds = [];
    for (const [mid, set] of Object.entries(allowed)) {
      if (set && set.size > 0) {
        allowed_operations[mid] = Array.from(set);
        moduleIds.push(mid);
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.post('/site-admin-grants/upsert', {
          site_id: siteId,
          user_id: selectedUser.user_id,
          allowed_operations,
          modules: moduleIds,
        });
        toast.success(`Updated grant for ${selectedUser.email || selectedUser.user_id}`);
      } else {
        await apiClient.post('/site-admin-grants/invite', {
          site_id: siteId,
          email: selectedUser.email,
          allowed_operations,
          modules: moduleIds,
        });
        toast.success(`Promoted ${selectedUser.email} to Site Admin of ${siteName}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save grant');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return eligibleUsers;
    const q = search.toLowerCase();
    return eligibleUsers.filter((u) =>
      (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q),
    );
  }, [eligibleUsers, search]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`grant-dialog-${siteId}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            {isEdit ? `Edit Site Admin grant — ${siteName}` : `Add Site Admin to ${siteName}`}
          </DialogTitle>
        </DialogHeader>

        {loadingMeta ? (
          <div className="flex items-center gap-2 text-stone-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* User pick (only on create) */}
            {!isEdit && (
              <div>
                <Label className="text-xs">Pick user</Label>
                <div className="relative mt-1">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-stone-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or email…"
                    className="pl-8"
                    data-testid="grant-user-search"
                  />
                </div>
                <div className="mt-2 max-h-44 overflow-y-auto border rounded-md">
                  {filteredUsers.length === 0 ? (
                    <div className="p-3 text-xs text-stone-500 italic">No eligible users.</div>
                  ) : filteredUsers.slice(0, 100).map((u) => (
                    <button
                      key={u.user_id}
                      onClick={() => setSelectedUser(u)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between border-b last:border-b-0 ${
                        selectedUser?.user_id === u.user_id ? 'bg-emerald-50' : 'hover:bg-stone-50'
                      }`}
                      data-testid={`user-pick-${u.user_id}`}
                    >
                      <div>
                        <div className="font-semibold text-stone-900">{u.name}</div>
                        <div className="text-stone-500 font-mono text-[10px]">{u.email}</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px]">{u.role || '—'}</Badge>
                        {selectedUser?.user_id === u.user_id && (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">picked</Badge>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                {filteredUsers.length > 100 && (
                  <p className="text-[10px] text-stone-500 mt-1">Showing first 100. Refine search to narrow.</p>
                )}
              </div>
            )}

            {isEdit && (
              <div className="rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-sm">
                <div className="font-semibold">{selectedUser?.name}</div>
                <div className="text-xs text-stone-500 font-mono">{selectedUser?.email}</div>
              </div>
            )}

            {/* Operation matrix */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label className="text-xs">Allowed operations matrix</Label>
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">
                  {totalOps} ops selected
                </Badge>
              </div>
              <div className="border rounded-md max-h-[40vh] overflow-y-auto">
                {modules.map((mod) => {
                  const set = allowed[mod.module_id] || new Set();
                  const opIds = (mod.operations || []).map((o) => o.op_id);
                  const allFilled = opIds.length > 0 && opIds.every((id) => set.has(id));
                  const someFilled = opIds.some((id) => set.has(id));
                  const isOpen = expandedModule === mod.module_id;
                  return (
                    <div key={mod.module_id} className="border-b last:border-b-0">
                      <button
                        type="button"
                        onClick={() => setExpandedModule(isOpen ? null : mod.module_id)}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-stone-50"
                        data-testid={`module-row-${mod.module_id}`}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); toggleModuleAll(mod); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); toggleModuleAll(mod); } }}
                            data-testid={`module-toggle-${mod.module_id}`}
                            className="cursor-pointer"
                          >
                            {allFilled
                              ? <CheckSquare className="h-4 w-4 text-emerald-600" />
                              : someFilled
                                ? <CheckSquare className="h-4 w-4 text-amber-500" />
                                : <Square className="h-4 w-4 text-stone-400" />}
                          </span>
                          <span className="font-semibold">{mod.module_name}</span>
                          {opIds.length > 0 && (
                            <Badge variant="outline" className="text-[10px]">
                              {set.size}/{opIds.length}
                            </Badge>
                          )}
                          {opIds.length === 0 && (
                            <span className="text-[10px] text-stone-400 italic">no operations</span>
                          )}
                        </div>
                        <span className="text-xs text-stone-400 font-mono">{mod.module_id}</span>
                      </button>
                      {isOpen && opIds.length > 0 && (
                        <div className="px-6 pb-3 grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {(mod.operations || []).map((op) => {
                            const checked = set.has(op.op_id);
                            return (
                              <label
                                key={op.op_id}
                                className="flex items-center gap-2 text-xs cursor-pointer hover:bg-stone-50 px-2 py-1 rounded"
                                data-testid={`op-toggle-${mod.module_id}-${op.op_id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleOp(mod.module_id, op.op_id)}
                                  className="accent-emerald-600"
                                />
                                <span className={checked ? 'text-stone-900 font-semibold' : 'text-stone-700'}>{op.op_name}</span>
                                <span className="text-[10px] font-mono text-stone-400 ml-auto">{op.op_id}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={onSave}
            disabled={saving || loadingMeta || !selectedUser}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="grant-save-btn"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : (isEdit ? 'Update Grant' : 'Promote to Site Admin')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
